import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/lib/supabase";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DriverProfile {
  readonly id: string;
  readonly user_id: string;
  readonly full_name: string;
  readonly phone: string;
  readonly region: string | null;
  readonly vehicle_type_portal: string | null;
  readonly status: string;
  readonly rating_average: number;
  readonly total_deliveries: number;
}

export interface DriverAuthContextValue {
  /** The authenticated driver profile, or null while loading / when unauthenticated */
  readonly driver: DriverProfile | null;
  /** True once a valid session exists and the driver profile has been loaded */
  readonly isAuthenticated: boolean;
  /** True while the initial session + profile check is in progress */
  readonly isLoading: boolean;
  /** Sign out and redirect to the driver login page */
  readonly signOut: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const DriverAuthContext = createContext<DriverAuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface DriverAuthProviderProps {
  readonly children: ReactNode;
}

export function DriverAuthProvider({ children }: DriverAuthProviderProps) {
  const [driver, setDriver] = useState<DriverProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch the driver profile from the internal API endpoint
  const fetchDriverProfile = useCallback(async () => {
    try {
      const {
        data: { user: authUser },
        error: authError,
      } = await supabase.auth.getUser();

      if (!authUser || authError) {
        setDriver(null);
        return;
      }

      const response = await fetch("/api/drivers/me", {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!response.ok) {
        // 401 / 403 / 404 — treat as unauthenticated for this portal
        setDriver(null);
        return;
      }

      const data: DriverProfile = await response.json();
      setDriver(data);
    } catch {
      // Network or unexpected error — treat as unauthenticated
      setDriver(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDriverProfile();

    // Re-check when auth state changes (login / logout).
    // Skip INITIAL_SESSION — fetchDriverProfile() above handles the first load.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "INITIAL_SESSION") return;
      if (session) {
        fetchDriverProfile();
      } else {
        setDriver(null);
        setIsLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchDriverProfile]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    // Hard redirect clears all React state
    window.location.href = "/livreur/connexion";
  }, []);

  const value = useMemo<DriverAuthContextValue>(
    () => ({
      driver,
      isAuthenticated: driver !== null,
      isLoading,
      signOut,
    }),
    [driver, isLoading, signOut],
  );

  return (
    <DriverAuthContext.Provider value={value}>
      {children}
    </DriverAuthContext.Provider>
  );
}
